import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { buildApiUrl } from '../../services/apiHelpers';

export default function DoctorQueue() {
  const { scheduleId } = useParams();
  const { auth } = useAuth();
  const CLINIC_ID = auth?.clinic?.id || null;

  const [queue, setQueue] = useState({ current: null, next: null, waiting: [] });
  const [error, setError] = useState("");

  const fetchQueue = async () => {
    try {
      if (!CLINIC_ID) {
        setError("Clinic session not found. Please log in.");
        return;
      }

      const res = await fetch(
        buildApiUrl(`appointments/live-queue?scheduleId=${scheduleId}`),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Clinic-Id": CLINIC_ID
          },
        }
      );

      if (!res.ok) throw new Error(await res.text() || "Failed to fetch queue");

      const data = await res.json();
      setQueue({
        current: data.current || null,
        next: data.next || null,
        waiting: data.waiting || []
      });
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 3000);
    return () => clearInterval(interval);
  }, [scheduleId]);

  return (
    <div style={page}>
      <header style={headerStyle}>
        <h2 style={title}>Live Queue Status</h2>
        <div style={badgeContainer}>
          <span style={badge}>Session: {scheduleId}</span>
          <span style={activeBadge}>LIVE SYNC ON</span>
        </div>
      </header>

      {error && <div style={errorBanner}>⚠️ {error}</div>}

      <div style={grid}>
        <QueueCard title="CONSULTING NOW" patient={queue.current} color="#16a34a" isCurrent={true} />
        <QueueCard title="UP NEXT" patient={queue.next} color="#f59e0b" />

        <div style={waitingCard}>
          <h3 style={sectionTitle}>Waitlist</h3>
          {queue.waiting?.length === 0 && !error && <p style={emptyText}>Waiting room empty</p>}
          <div style={waitingList}>
            {queue.waiting?.map((p, i) => (
              <div key={i} style={waitingRow}>
                <span style={tokenNo}>#{p.tokenNo}</span>
                <span style={patientNameText}>{p.patientName}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const QueueCard = ({ title, patient, color, isCurrent }) => (
  <div style={{ ...cardBase, borderTop: `6px solid ${color}` }}>
    <h4 style={cardLabel}>{title}</h4>
    {patient ? (
      <>
        <h2 style={{ color, fontSize: "42px", margin: 0, fontWeight: "900" }}>#{patient.tokenNo}</h2>
        <p style={currentPatientName}>{patient.patientName}</p>
        {isCurrent && <span style={pulseDot}></span>}
      </>
    ) : <p style={noneText}>Waiting for arrivals...</p>}
  </div>
);

/* ================= MODERN STYLES ================= */
const page = { padding: "40px", background: "#f8fafc", minHeight: "100vh", fontFamily: "Inter, sans-serif" };
const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" };
const title = { margin: 0, fontSize: "28px", fontWeight: "800", color: "#0f172a" };
const badgeContainer = { display: "flex", gap: "10px" };
const badge = { background: "#e2e8f0", padding: "6px 12px", borderRadius: "8px", fontSize: "13px", color: "#475569", fontWeight: "bold" };
const activeBadge = { background: "#dcfce7", color: "#15803d", padding: "6px 12px", borderRadius: "8px", fontSize: "11px", fontWeight: "900" };
const errorBanner = { background: "#fee2e2", color: "#dc2626", padding: "15px", borderRadius: "12px", marginBottom: "25px", fontWeight: "bold", textAlign: "center" };
const grid = { display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "25px" };
const cardBase = { background: "white", padding: "30px", borderRadius: "20px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)", position: "relative" };
const waitingCard = { background: "white", padding: "30px", borderRadius: "20px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)" };
const cardLabel = { margin: 0, fontSize: "12px", color: "#64748b", fontWeight: "900" };
const sectionTitle = { margin: "0 0 20px 0", fontSize: "18px", fontWeight: "800", color: "#1e293b" };
const waitingList = { display: "flex", flexDirection: "column", gap: "12px" };
const waitingRow = { padding: "12px 0", borderBottom: "1px solid #f1f5f9", display: "flex", gap: "15px", alignItems: "center" };
const tokenNo = { fontWeight: "900", color: "#93c5fd", background: "#eff6ff", padding: "4px 8px", borderRadius: "6px" };
const patientNameText = { fontWeight: "700", color: "#334155" };
const currentPatientName = { fontSize: "18px", fontWeight: "700", color: "#1e293b" };
const noneText = { color: "#94a3b8", fontStyle: "italic" };
const emptyText = { textAlign: "center", color: "#94a3b8", padding: "20px" };
const pulseDot = { height: "10px", width: "10px", background: "#10b981", borderRadius: "50%", position: "absolute", top: "20px", right: "20px", boxShadow: "0 0 8px #10b981" }; 
